import './TableOfContentSidePanel.scss';

import NavigationDialog from '@components/Common/NavigationDialog/NavigationDialog';
import { useDeckContext } from '@context/DeckContext';
import { useTOCContext } from '@context/TOCContext';
import DeckSelector from '@deckManagement/components/Common/SidePanel/DeckSelector';
import TableOfContentsSelector from '@deckManagement/components/Common/SidePanel/TableOfContentsSelector';
import useTocSelectorConfig from '@deckManagement/hooks/useTocSelectorConfig';
import { ViewType } from '@deckManagement/types/TableOfContentsSelector.types';
import useNavigationGuard from '@hooks/useNavigationGuard';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { TOCViewType } from 'src/components/Common/CommonEnums';

/**
 * Side panel component for Table of Contents navigation
 * Displays navigation options for deck management with support for active/inactive page views
 */
const TableOfContentSidePanel = () => {
  const { deckId } = useParams() as { deckId: string };
  const location = useLocation();
  const navigate = useNavigate();
  const { availableDecks } = useDeckContext();
  const { currentView, setView, hasUnsavedChanges, getTocItemsByGroup } = useTOCContext();

  const { showConfirmDialog, navigationDialogProps } = useNavigationGuard({
    dialogTitle: 'Unsaved Changes in Page Library',
    dialogText: 'You have unsaved changes in the Page Library. Are you sure you want to leave this page?',
    cancelText: 'Stay on Page',
    confirmText: 'Leave Without Saving',
    additionalText: 'Your changes will be lost if you continue without saving.',
    hasUnsavedChanges,
  });

  const tocPath = `/deck-preparation/decks/${deckId}/toc`;

  /**
   * Handle navigation with view management and unsaved changes check
   * @param {string} path - The navigation path
   * @param {TOCViewType} [view] - The view to set (active/inactive)
   */
  const handleNavigation = (path: string, view?: TOCViewType) => {
    // If we're navigating to a different page (not just changing view) and have unsaved changes
    if (path !== location.pathname && hasUnsavedChanges()) {
      // Show confirmation dialog
      showConfirmDialog(() => {
        if (view) {
          setView(view);
        }
        navigate(path);
      });
      return;
    }

    // No unsaved changes or same page - navigate normally
    if (view) {
      setView(view);
    }

    if (path !== location.pathname) {
      navigate(path);
    }
  };

  const menuItems = [
    {
      label: 'Active Pages',
      path: tocPath,
      view: 'active' as TOCViewType,
      isActive: location.pathname === tocPath && currentView === 'active',
    },
    {
      label: 'Inactive Pages',
      path: tocPath,
      view: 'inactive' as TOCViewType,
      isActive: location.pathname === tocPath && currentView === 'inactive',
    },
    {
      label: 'Scenario Management',
      path: `/deck-preparation/decks/${deckId}/scenario-management`,
      isActive: location.pathname === `/deck-preparation/decks/${deckId}/scenario-management`,
      hasDivider: true,
    },
    // { TODO: Restore when Dimension Mgmt is implemented
    //   label: 'Dimension Management',
    //   path: `/deck-preparation/decks/${deckId}/dimension-management`,
    //   isActive: location.pathname === `/deck-preparation/decks/${deckId}/dimension-management`,
    //   hasDivider: true,
    // },
  ];

  /**
   * Memoized TOC configuration items for the current Deck and Page View
   */
  const currTocSelectorConfig = useTocSelectorConfig(deckId, getTocItemsByGroup);

  return (
    <Box>
      <TableOfContentsSelector
        deckId={deckId}
        tocPageItems={currTocSelectorConfig.currPageItems}
        viewType={ViewType.TOC_VIEW}
        tocPageGroupLookup={currTocSelectorConfig.groupLookupValues}
      />
      <DeckSelector deckNavItems={availableDecks} />

      <Box className='toc-side-navigation'>
        <List>
          {menuItems.map((item) => (
            <Box key={item.label}>
              <ListItem
                component='button'
                onClick={() => handleNavigation(item.path, item.view)}
                className={`nav-list-item ${item.isActive ? 'active' : ''}`}
              >
                <ListItemText primary={item.label} className={item.isActive ? 'text-active' : 'text-inactive'} />
              </ListItem>
              {item.hasDivider && <Divider className='nav-divider' />}
            </Box>
          ))}
        </List>
      </Box>

      <NavigationDialog {...navigationDialogProps} />
    </Box>
  );
};

export default TableOfContentSidePanel;
